import React, { useEffect, useState } from "react";

import { getSessionToken } from "@/services/sessionToken";
import { postSessionToOpener } from "@/services/embedSession";

/**
 * Where the sign-in popup lands once the user is authenticated (#11021).
 *
 * This route sits inside the authenticated half of `AuthWall`, which is what
 * makes it work for both entry paths without any branching here:
 *
 * - Already signed in when the popup opened — `AuthWall` renders the
 *   authenticated tree immediately, so we post and close in one frame.
 * - Not signed in — `AuthWall` captures this path as `redirectAfterLogin` and
 *   shows the ordinary login screen. Because the popup is a *top-level* window,
 *   SSO, Google and password sign-in all run here unmodified, and the existing
 *   token resolver sends the user back to this route afterwards.
 *
 * The popup reads the session from its own cookie: it is a first-party window,
 * so unlike the frame it has one.
 */
const EmbedAuthComplete: React.FC = () => {
  const [orphaned, setOrphaned] = useState(false);

  useEffect(() => {
    const token = getSessionToken();
    if (!token) return;

    if (postSessionToOpener(token)) {
      window.close();
    } else {
      // Reached directly rather than through the embed flow — there is no frame
      // waiting on us, so closing would just blank the tab.
      setOrphaned(true);
    }
  }, []);

  return (
    <div className="flex h-full w-full items-center justify-center bg-white">
      <div className="text-sm text-[#6E7480]">
        {orphaned ? (
          <>
            You are signed in to Finpilot. You can close this window and return
            to the page you came from.
          </>
        ) : (
          <>Signing you in…</>
        )}
      </div>
    </div>
  );
};

export default EmbedAuthComplete;
